<!-- src/pages/AboutPage.vue -->
<template>
    <main class="about" aria-label="About M.C Bridge of Hope">
        <!-- HERO -->
        <section class="hero" aria-labelledby="about-title">
            <div class="hero-backdrop" aria-hidden="true">
                <img class="hero-backdropLogo" :src="brandLogo" alt="" />
            </div>

            <div class="shell">
                <div class="hero-shell">
                    <div class="hero-copy">
                        <div class="hero-top">
                            <p class="eyebrow">M.C • Bridge of Hope</p>
                            <h1 id="about-title" class="hero-title">About us</h1>
                        </div>

                        <p class="hero-sub">
                            The M.C Bridge of Hope: Youth empowerment and leadership program is designed to
                            nurture personal growth, develop leadership skills, and strengthen a sense of community
                            among young people. Through a dynamic blend of workshops, mentorship, and service-based
                            learning, participants are encouraged to embrace education, build resilience, and envision a
                            brighter future for themselves and their communities.
                        </p>

                        <div class="hero-meta" aria-label="Our values">
                            <span class="pill"><span class="dot" aria-hidden="true"></span>Compassion</span>
                            <span class="pill"><span class="dot" aria-hidden="true"></span>Dignity</span>
                            <span class="pill"><span class="dot" aria-hidden="true"></span>Community</span>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- MISSION -->
        <section class="section">
            <div class="shell">
                <header class="section-head">
                    <h2 class="section-title">Our mission</h2>
                    <p class="section-sub">
                        Build bridges of hope through practical support, strong partnerships, and programs that uplift
                        people and strengthen families.
                    </p>
                </header>

                <div class="cards mission-cards">
                    <article class="card">
                        <h3 class="card-title-sm">Practical help</h3>
                        <p class="card-text">Essentials, referrals, and hands-on support for people doing it tough.</p>
                    </article>

                    <article class="card">
                        <h3 class="card-title-sm">Connection</h3>
                        <p class="card-text">Linking people to services, community networks, and trusted guidance.</p>
                    </article>

                    <article class="card">
                        <h3 class="card-title-sm">Celebration</h3>
                        <p class="card-text">Uplifting individuals who create positive change and inspire others.</p>
                    </article>
                </div>
            </div>
        </section>

        <!-- STORY -->
        <section class="section soft">
            <div class="shell narrow">
                <header class="section-head centred">
                    <h2 class="section-title">Our story</h2>
                    <p class="section-sub">
                        Bridge of Hope exists to serve with compassion and dignity — helping people navigate hard
                        moments with practical care and community support.
                    </p>
                </header>
            </div>
        </section>

        <!-- VALUES -->
        <section class="section">
            <div class="shell">
                <header class="section-head">
                    <h2 class="section-title">What we stand for</h2>
                    <p class="section-sub">Guiding values that shape how we serve.</p>
                </header>

                <div class="cards values-cards">
                    <article class="card">
                        <h3 class="card-title-sm">Compassion</h3>
                        <p class="card-text">We show up with empathy, care, and respect.</p>
                    </article>

                    <article class="card">
                        <h3 class="card-title-sm">Dignity</h3>
                        <p class="card-text">Support is delivered with honour, privacy, and choice.</p>
                    </article>

                    <article class="card">
                        <h3 class="card-title-sm">Integrity</h3>
                        <p class="card-text">We act transparently, responsibly, and consistently.</p>
                    </article>

                    <article class="card">
                        <h3 class="card-title-sm">Community</h3>
                        <p class="card-text">We collaborate to strengthen families and local networks.</p>
                    </article>
                </div>
            </div>
        </section>

        <!-- TEAM -->
        <section class="section soft">
            <div class="shell">
                <header class="section-head">
                    <h2 class="section-title">Our team</h2>
                    <p class="section-sub">A community-first team supported by volunteers, partners, and supporters.</p>
                </header>

                <div class="team">
                    <article class="person">
                        <div class="avatar" aria-hidden="true"><span>MC</span></div>

                        <div class="person-copy">
                            <h3 class="person-name">M.C</h3>
                            <p class="person-role">Founder</p>
                            <p class="person-bio">
                                Leads the vision with a focus on practical support and community care.
                            </p>
                        </div>
                    </article>

                    <article class="person">
                        <div class="avatar" aria-hidden="true"><span>PC</span></div>

                        <div class="person-copy">
                            <h3 class="person-name">Program Coordinator</h3>
                            <p class="person-role">Programs</p>
                            <p class="person-bio">
                                Coordinates outreach, community support, and partnerships for local impact.
                            </p>
                        </div>
                    </article>

                    <article class="person">
                        <div class="avatar" aria-hidden="true"><span>VS</span></div>

                        <div class="person-copy">
                            <h3 class="person-name">Volunteer Support</h3>
                            <p class="person-role">Community</p>
                            <p class="person-bio">
                                Supports volunteers and helps create a welcoming, safe environment.
                            </p>
                        </div>
                    </article>
                </div>
            </div>
        </section>

        <!-- CTA -->
        <section class="cta" aria-label="Get involved call to action">
            <div class="shell">
                <div class="cta-shell">
                    <div class="cta-copy">
                        <h2 class="cta-title">Join us in building a bridge of hope.</h2>

                        <p class="cta-sub">
                            Whether you donate, volunteer, or partner with us — your contribution helps create real
                            support and lasting change.
                        </p>

                        <div class="cta-actions">
                            <RouterLink class="btn primary" to="/donate">Donate</RouterLink>
                            <RouterLink class="btn ghost" to="/contact">Contact</RouterLink>
                        </div>
                    </div>

                    <div class="cta-media" role="img" :aria-label="ctaAlt">
                        <div v-if="loadingCta" class="img-skeleton"></div>

                        <img v-else class="cta-img" :src="ctaImg" :alt="ctaAlt" loading="lazy" decoding="async"
                            @error="onCtaError" />
                    </div>
                </div>
            </div>
        </section>

        <!-- CREDITS -->
        <section class="credits" aria-label="Image credits">
            <div class="shell"></div>
        </section>
    </main>
</template>

<script>
import { useHead } from "@unhead/vue";
import maryChanLogo from "@/assets/logo7.png";

export default {
    name: "AboutPage",

    setup() {
        useHead({
            title: "About M.C Bridge of Hope | Youth Empowerment & Community Support",

            meta: [
                {
                    name: "description",
                    content:
                        "Learn about M.C Bridge of Hope, our mission, values, story, and youth empowerment and leadership program supporting young people, families, and communities.",
                },
                {
                    name: "keywords",
                    content:
                        "About M.C Bridge of Hope, M.C charity, youth empowerment Australia, community support, leadership program, mentorship, practical help, volunteer support",
                },
                {
                    name: "author",
                    content: "M.C Bridge of Hope",
                },
                {
                    name: "robots",
                    content:
                        "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
                },

                {
                    property: "og:title",
                    content:
                        "About M.C Bridge of Hope | Youth Empowerment & Community Support",
                },
                {
                    property: "og:description",
                    content:
                        "Learn about our mission, values, story, and commitment to youth empowerment, leadership, and community support.",
                },
                {
                    property: "og:type",
                    content: "website",
                },
                {
                    property: "og:url",
                    content: "https://marychan.org.au/about",
                },
                {
                    property: "og:image",
                    content:
                        "https://marychan.org.au/logo7.jpeg",
                },
                {
                    property: "og:image:alt",
                    content:
                        "M.C Bridge of Hope community support and youth empowerment",
                },
                {
                    property: "og:site_name",
                    content: "M.C Bridge of Hope",
                },
                {
                    property: "og:locale",
                    content: "en_AU",
                },

                {
                    name: "twitter:card",
                    content: "summary_large_image",
                },
                {
                    name: "twitter:title",
                    content:
                        "About M.C Bridge of Hope | Youth Empowerment & Community Support",
                },
                {
                    name: "twitter:description",
                    content:
                        "Our mission is to build bridges of hope through practical support, mentorship, leadership programs, and community care.",
                },
                {
                    name: "twitter:image",
                    content:
                        "https://marychan.org.au/logo7.jpeg",
                },
            ],

            link: [
                {
                    rel: "canonical",
                    href: "https://marychan.org.au/about",
                },
                {
                    rel: "alternate",
                    hrefLang: "en-au",
                    href: "https://marychan.org.au/about",
                },
                {
                    rel: "alternate",
                    hrefLang: "x-default",
                    href: "https://marychan.org.au/about",
                },
            ],

            script: [
                {
                    type: "application/ld+json",
                    children: JSON.stringify({
                        "@context": "https://schema.org",
                        "@graph": [
                            {
                                "@type": "Organization",
                                "@id":
                                    "https://marychan.org.au/#organization",
                                name: "M.C Bridge of Hope",
                                url: "https://marychan.org.au/",
                                logo: "https://marychan.org.au/logo7.jpeg",
                                description:
                                    "M.C Bridge of Hope supports practical care, community outreach, youth empowerment and leadership programs in Australia.",
                                founder: {
                                    "@type": "Person",
                                    name: "M.C",
                                },
                                areaServed: {
                                    "@type": "Country",
                                    name: "Australia",
                                },
                            },
                            {
                                "@type": "WebSite",
                                "@id": "https://marychan.org.au/#website",
                                url: "https://marychan.org.au/",
                                name: "M.C Bridge of Hope",
                                publisher: {
                                    "@id":
                                        "https://marychan.org.au/#organization",
                                },
                                inLanguage: "en-AU",
                            },
                            {
                                "@type": "AboutPage",
                                "@id":
                                    "https://marychan.org.au/about#webpage",
                                url: "https://marychan.org.au/about",
                                name:
                                    "About M.C Bridge of Hope | Youth Empowerment & Community Support",
                                description:
                                    "Learn about M.C Bridge of Hope, our mission, values, story, team, and youth empowerment and leadership program.",
                                isPartOf: {
                                    "@id":
                                        "https://marychan.org.au/#website",
                                },
                                about: {
                                    "@id":
                                        "https://marychan.org.au/#organization",
                                },
                                inLanguage: "en-AU",
                            },
                            {
                                "@type": "BreadcrumbList",
                                "@id":
                                    "https://marychan.org.au/about#breadcrumb",
                                itemListElement: [
                                    {
                                        "@type": "ListItem",
                                        position: 1,
                                        name: "Home",
                                        item: "https://marychan.org.au/",
                                    },
                                    {
                                        "@type": "ListItem",
                                        position: 2,
                                        name: "About",
                                        item:
                                            "https://marychan.org.au/about",
                                    },
                                ],
                            },
                        ],
                    }),
                },
            ],
        });

        return {};
    },

    data() {
        return {
            PEXELS_KEY:
                process.env.VUE_APP_PEXELS_KEY ||
                process.env.VITE_PEXELS_KEY ||
                "",
            brandLogo: maryChanLogo,
            ctaAlt: "Hands helping and community care",
            fallbackCta:
                "https://images.pexels.com/photos/6647019/pexels-photo-6647019.jpeg?auto=compress&cs=tinysrgb&w=1600&h=1000&dpr=1",
            ctaImg:
                "https://images.pexels.com/photos/6647019/pexels-photo-6647019.jpeg?auto=compress&cs=tinysrgb&w=1600&h=1000&dpr=1",
        };
    },

    computed: {
        loadingCta() {
            return !this.ctaImg;
        },
    },

    methods: {
        onCtaError() {
            this.ctaImg = this.fallbackCta;
        },

        async pexelsSearch(query, perPage = 1) {
            const url =
                `https://api.pexels.com/v1/search?query=${encodeURIComponent(query)}` +
                `&per_page=${perPage}&orientation=landscape`;

            const res = await fetch(url, {
                headers: {
                    Authorization: this.PEXELS_KEY,
                },
            });

            if (!res.ok) {
                throw new Error(`Pexels request failed: ${res.status}`);
            }

            const data = await res.json();

            return data?.photos || [];
        },

        buildPexelsImgUrl(photo, w = 1600, h = 1000) {
            const base =
                photo?.src?.large2x ||
                photo?.src?.large ||
                photo?.src?.landscape ||
                photo?.src?.medium ||
                "";

            if (!base) return "";

            const glue = base.includes("?") ? "&" : "?";

            return `${base}${glue}auto=compress&cs=tinysrgb&w=${w}&h=${h}&dpr=1`;
        },

        async loadDynamicImages() {
            if (!this.PEXELS_KEY) return;

            try {
                const cta = await this.pexelsSearch("hands helping support", 1);
                const ctaUrl = this.buildPexelsImgUrl(cta[0], 1600, 1000);

                if (ctaUrl) {
                    this.ctaImg = ctaUrl;
                }
            } catch {
                // keep fallback
            }
        },
    },

    mounted() {
        this.loadDynamicImages();
        window.scrollTo(0, 0);
    },
};
</script>

<style scoped lang="scss">
.about {
    --bg: #fbfbfc;
    --paper: rgba(255, 255, 255, 0.84);
    --paper-strong: rgba(255, 255, 255, 0.92);
    --ink: #0f172a;
    --muted: rgba(15, 23, 42, 0.68);
    --muted2: rgba(15, 23, 42, 0.52);
    --brand: #0b3f57;
    --accent: #b9996b;
    --rule: rgba(15, 23, 42, 0.1);
    --rule2: rgba(15, 23, 42, 0.06);
    --r-lg: 24px;
    --r-md: 18px;

    background:
        radial-gradient(900px 400px at 10% -10%,
            rgba(11, 63, 87, 0.07),
            transparent 60%),
        radial-gradient(800px 360px at 92% 8%,
            rgba(185, 153, 107, 0.08),
            transparent 58%),
        var(--bg);
    color: var(--ink);

    .shell {
        max-width: 1120px;
        margin: 0 auto;
        padding: 0 16px;

        @media (min-width: 480px) {
            padding: 0 18px;
        }

        @media (min-width: 720px) {
            padding: 0 22px;
        }

        @media (min-width: 1024px) {
            padding: 0 28px;
        }

        &.narrow {
            max-width: 860px;
        }
    }

    .eyebrow {
        margin: 0;
        font-weight: 900;
        letter-spacing: 0.14em;
        font-size: 11px;
        color: rgba(15, 23, 42, 0.58);
        text-transform: uppercase;

        @media (min-width: 720px) {
            font-size: 12px;
        }
    }

    .section-title {
        margin: 0 0 10px;
        font-size: clamp(24px, 2.2vw, 34px);
        line-height: 1.1;
        letter-spacing: -0.35px;
        font-weight: 950;
        color: var(--brand);
    }

    .section-sub {
        margin: 0;
        max-width: 68ch;
        line-height: 1.8;
        color: var(--muted);
    }

    .btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 10px;
        min-height: 44px;
        text-decoration: none;
        border-radius: 999px;
        padding: 11px 16px;
        font-weight: 900;
        border: 1px solid var(--rule);
        background: rgba(255, 255, 255, 0.86);
        color: var(--ink);
        transition:
            transform 140ms ease,
            box-shadow 140ms ease,
            background 140ms ease;
        backdrop-filter: blur(10px);

        &:hover {
            transform: translateY(-1px);
            box-shadow: 0 10px 22px rgba(15, 23, 42, 0.1);
            background: rgba(255, 255, 255, 0.94);
        }

        &.primary {
            background: linear-gradient(180deg,
                    rgba(15, 23, 42, 0.98),
                    rgba(15, 23, 42, 0.9));
            color: #fff;
            border-color: rgba(15, 23, 42, 0.22);
        }

        &.ghost {
            background: rgba(255, 255, 255, 0.72);
            color: var(--brand);
        }
    }

    .img-skeleton {
        width: 100%;
        height: 100%;
        border-radius: inherit;
        background: linear-gradient(90deg,
                rgba(15, 23, 42, 0.05),
                rgba(15, 23, 42, 0.09),
                rgba(15, 23, 42, 0.05));
        background-size: 200% 100%;
        animation: shimmer 1.1s infinite linear;
    }

    @keyframes shimmer {
        0% {
            background-position: 0% 0;
        }

        100% {
            background-position: 200% 0;
        }
    }

    .hero {
        position: relative;
        overflow: clip;
        padding: 32px 0 20px;

        @media (min-width: 480px) {
            padding: 38px 0 24px;
        }

        @media (min-width: 720px) {
            padding: 48px 0 30px;
        }

        @media (min-width: 1024px) {
            padding: 60px 0 36px;
        }

        .hero-backdrop {
            position: absolute;
            inset: 0;
            pointer-events: none;
            z-index: 0;
            background:
                radial-gradient(900px 420px at 12% -10%,
                    rgba(11, 63, 87, 0.1),
                    transparent 60%),
                radial-gradient(760px 360px at 92% 8%,
                    rgba(185, 153, 107, 0.12),
                    transparent 58%),
                linear-gradient(180deg,
                    rgba(15, 23, 42, 0.04),
                    transparent 60%);
        }

        .hero-backdropLogo {
            position: absolute;
            right: 5%;
            top: -40px;
            width: 50vw;
            z-index: 5;
            opacity: 0.08;
            transform: rotate(-12deg);

            @media (max-width: 720px) {
                top: -60px;
                width: 100vw;
                height: 300px;
                margin-bottom: 100px;
                opacity: 0.1;
            }

            @media (max-width: 1024px) {
                width: 70vw;
                top: 40px;
                right: 0%;
            }
        }

        .hero-shell {
            position: relative;
            z-index: 1;
            display: grid;
            justify-items: start;
        }

        .hero-copy {
            width: 100%;
            max-width: 760px;
            padding: 18px 0;
            text-align: left;

            @media (min-width: 480px) {
                padding: 22px 0;
            }

            @media (min-width: 720px) {
                padding: 28px 0;
            }
        }

        .hero-top {
            display: grid;
            gap: 10px;
            margin-bottom: 14px;
            justify-items: start;
        }

        .hero-title {
            margin: 0;
            max-width: 12ch;
            font-size: clamp(34px, 6vw, 68px);
            line-height: 0.98;
            letter-spacing: -1px;
            font-weight: 1000;
            color: var(--brand);
        }

        .hero-sub {
            margin: 0 0 18px;
            max-width: 62ch;
            line-height: 1.9;
            color: var(--muted);
            font-size: clamp(15px, 1.4vw, 18px);
        }

        .hero-meta {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
            align-items: center;

            .pill {
                display: inline-flex;
                align-items: center;
                gap: 10px;
                padding: 9px 12px;
                border-radius: 999px;
                background: rgba(255, 255, 255, 0.88);
                border: 1px solid var(--rule2);
                font-weight: 900;
                font-size: 12px;
                color: rgba(15, 23, 42, 0.72);
                box-shadow: 0 8px 18px rgba(15, 23, 42, 0.05);

                @media (min-width: 720px) {
                    font-size: 13px;
                }

                .dot {
                    width: 8px;
                    height: 8px;
                    border-radius: 999px;
                    background: linear-gradient(180deg,
                            rgba(185, 153, 107, 1),
                            rgba(11, 63, 87, 0.95));
                }
            }
        }
    }

    .section {
        padding: 24px 0;

        @media (min-width: 480px) {
            padding: 28px 0;
        }

        @media (min-width: 720px) {
            padding: 34px 0;
        }

        &.soft {
            background: rgba(255, 255, 255, 0.46);
            border-top: 1px solid var(--rule2);
            border-bottom: 1px solid var(--rule2);
        }

        .section-head {
            display: grid;
            gap: 8px;
            margin-bottom: 18px;
            justify-items: start;
            text-align: left;

            &.centred {
                justify-items: center;
                text-align: center;

                .section-sub {
                    margin-left: auto;
                    margin-right: auto;
                }
            }
        }

        .cards {
            display: grid;
            grid-template-columns: 1fr;
            gap: 12px;

            @media (min-width: 720px) {
                gap: 14px;
            }
        }

        .mission-cards {
            @media (min-width: 1024px) {
                grid-template-columns: repeat(3, 1fr);
            }
        }

        .values-cards {
            @media (min-width: 640px) {
                grid-template-columns: repeat(2, 1fr);
            }

            @media (min-width: 1024px) {
                grid-template-columns: repeat(4, 1fr);
            }
        }

        .card {
            position: relative;
            overflow: hidden;
            border-radius: calc(var(--r-lg) + 4px);
            background: var(--paper-strong);
            border: 1px solid var(--rule2);
            padding: 18px 18px 16px;
            box-shadow: 0 12px 28px rgba(15, 23, 42, 0.06);
            transition:
                transform 160ms ease,
                box-shadow 160ms ease;
            text-align: left;

            &::before {
                content: "";
                position: absolute;
                left: 0;
                right: 0;
                top: 0;
                height: 4px;
                background: linear-gradient(90deg,
                        rgba(11, 63, 87, 0.95),
                        rgba(185, 153, 107, 0.95));
            }

            &:hover {
                transform: translateY(-4px);
                box-shadow: 0 18px 40px rgba(15, 23, 42, 0.1);
            }

            .card-title-sm {
                margin: 6px 0 10px;
                font-weight: 1000;
                letter-spacing: -0.25px;
                color: var(--brand);
            }

            .card-text {
                margin: 0;
                color: rgba(15, 23, 42, 0.72);
                line-height: 1.85;
            }
        }
    }

    .team {
        display: grid;
        grid-template-columns: 1fr;
        gap: 12px;

        @media (min-width: 640px) {
            grid-template-columns: repeat(2, 1fr);
            gap: 14px;
        }

        @media (min-width: 1024px) {
            grid-template-columns: repeat(3, 1fr);
        }

        .person {
            display: grid;
            grid-template-columns: 60px 1fr;
            gap: 12px;
            align-items: start;
            padding: 16px;
            border-radius: calc(var(--r-lg) + 4px);
            background: var(--paper);
            border: 1px solid var(--rule2);
            box-shadow: 0 10px 24px rgba(15, 23, 42, 0.06);
            text-align: left;

            .avatar {
                width: 60px;
                height: 60px;
                border-radius: var(--r-md);
                border: 1px solid var(--rule2);
                background:
                    radial-gradient(18px 18px at 25% 30%,
                        rgba(185, 153, 107, 0.45),
                        transparent 60%),
                    linear-gradient(180deg,
                        rgba(11, 63, 87, 0.16),
                        rgba(185, 153, 107, 0.14));
                display: grid;
                place-items: center;
                font-weight: 1000;
                color: rgba(15, 23, 42, 0.88);
            }

            .person-copy {
                min-width: 0;
            }

            .person-name {
                margin: 0 0 4px;
                font-weight: 1000;
                letter-spacing: -0.2px;
                color: var(--brand);
            }

            .person-role {
                margin: 0 0 8px;
                color: rgba(15, 23, 42, 0.58);
                font-weight: 900;
                font-size: 12px;
                text-transform: uppercase;
                letter-spacing: 0.45px;
            }

            .person-bio {
                margin: 0;
                color: rgba(15, 23, 42, 0.72);
                line-height: 1.8;
            }
        }
    }

    .cta {
        padding: 26px 0 34px;

        .cta-shell {
            border-radius: calc(var(--r-lg) + 10px);
            background: rgba(255, 255, 255, 0.82);
            border: 1px solid var(--rule2);
            overflow: hidden;
            display: grid;
            gap: 10px;
            box-shadow: 0 16px 38px rgba(15, 23, 42, 0.08);

            @media (min-width: 1024px) {
                grid-template-columns: 1.08fr 0.92fr;
                align-items: stretch;
            }
        }

        .cta-copy {
            padding: 18px;
            text-align: left;

            @media (min-width: 720px) {
                padding: 22px;
            }
        }

        .cta-title {
            margin: 0 0 10px;
            font-weight: 1000;
            font-size: clamp(24px, 2.4vw, 36px);
            line-height: 1.08;
            letter-spacing: -0.5px;
            color: var(--brand);
        }

        .cta-sub {
            margin: 0 0 14px;
            color: rgba(15, 23, 42, 0.72);
            line-height: 1.85;
            max-width: 60ch;
        }

        .cta-actions {
            display: flex;
            flex-wrap: wrap;
            gap: 10px;
            align-items: center;
        }

        .cta-media {
            min-height: 260px;
            background:
                radial-gradient(360px 180px at 15% 25%,
                    rgba(11, 63, 87, 0.1),
                    transparent 65%),
                radial-gradient(320px 160px at 85% 30%,
                    rgba(185, 153, 107, 0.1),
                    transparent 62%),
                linear-gradient(180deg,
                    rgba(255, 255, 255, 0.55),
                    rgba(245, 246, 248, 0.95));

            @media (min-width: 1024px) {
                min-height: 100%;
            }
        }

        .cta-img {
            display: block;
            width: 100%;
            height: 100%;
            min-height: 260px;
            object-fit: cover;
        }
    }

    .credits {
        padding: 0 0 28px;

        .credits-text {
            margin: 0;
            color: var(--muted2);
            font-size: 12px;
            text-align: left;
        }
    }

    @media (prefers-reduced-motion: reduce) {

        .btn,
        .card,
        .img-skeleton {
            transition: none !important;
            animation: none !important;
        }

        .card:hover {
            transform: none !important;
        }
    }
}
</style>